import { HttpErrorResponse } from '@angular/common/http';

export type AuthErrorKind =
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'rate-limit'
  | 'timeout'
  | 'offline'
  | 'server'
  | 'unexpected';

export class AuthApiError extends Error {
  constructor(
    readonly kind: AuthErrorKind,
    readonly status: number,
    readonly retryAfterSeconds?: number,
  ) {
    super(authErrorMessage(kind, retryAfterSeconds));
    this.name = 'AuthApiError';
  }
}

export function mapAuthError(error: unknown): AuthApiError {
  if (error instanceof AuthApiError) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return new AuthApiError(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'offline'
          : 'unexpected',
        error.status,
      );
    }
    if (error.status === 400) return new AuthApiError('validation', 400);
    if (error.status === 401) return new AuthApiError('unauthorized', 401);
    if (error.status === 403) return new AuthApiError('forbidden', 403);
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      return new AuthApiError(
        'rate-limit',
        429,
        Number.isFinite(retryAfter) ? retryAfter : undefined,
      );
    }
    if (error.status >= 500) return new AuthApiError('server', error.status);
  }

  if (error instanceof Error && error.name === 'TimeoutError') {
    return new AuthApiError('timeout', 0);
  }

  return new AuthApiError('unexpected', 0);
}

function authErrorMessage(
  kind: AuthErrorKind,
  retryAfterSeconds?: number,
): string {
  switch (kind) {
    case 'validation':
      return 'Revisa el correo y la contraseña e intenta de nuevo.';
    case 'unauthorized':
      return 'Credenciales incorrectas. Verifica tus datos e intenta de nuevo.';
    case 'forbidden':
      return 'Tu cuenta no tiene acceso a este sistema.';
    case 'rate-limit':
      return retryAfterSeconds
        ? `Demasiados intentos. Intenta de nuevo en ${retryAfterSeconds} segundos.`
        : 'Demasiados intentos. Espera un momento antes de volver a intentar.';
    case 'timeout':
      return 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.';
    case 'offline':
      return 'No hay conexión a internet. Verifica tu red e intenta de nuevo.';
    case 'server':
    case 'unexpected':
      return 'No fue posible completar la solicitud. Intenta de nuevo más tarde.';
  }
}
